import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag, TagTypeProps } from "components/DesignSystem/Tag/Tag";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { DeleteIcon } from "components/icons/delete";
import Pencil from "components/icons/pencil";
import { Preview } from "components/PreviewModal";
import { DISPLAY_DATE } from "constants/date";
import { TAX_ROUTE } from "constants/tax";
import dayjs from "dayjs";
import { REGISTRATION_TYPE, REGISTRATION_TYPE_BADGE } from "dictionaries";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useMultipleFileDetails } from "hooks/useMultipleFileDetails";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Link, useParams } from "react-router-dom";
import CopyIcon from "static/images/CopyPurple.svg";
import {
  useGetEntityAllStateDataQuery,
  useGetEntityDataAttributesQuery,
} from "store/apis/entity";
import { useGetStateRegistrationsQuery } from "store/apis/StateRegistrations";
import { getStateAttentionCount } from "util/getStateAttentionCount";
import {
  EntityDataAttribute,
  EntityStateData,
  state_registration_type,
} from "types/Models/entity";
import { FileObject } from "types/Models/fileObject";
import { FileIcon } from "components/design/FileIcon";
import AddJurisdiction from "./AddJurisdiction";
import DeleteJurisdictions from "./DeleteJurisdictions";
import EditJurisdiction from "./EditJurisdiction";
import WithdrawJurisdiction from "./WithdrawJurisdiction";
import ConditionalToolTip from "components/design/conditionalToolTip";
import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import { ArrowRight } from "components/icons/ArrowRight";
import { Warning } from "components/icons/Warning";
import { MenuDotsVertical } from "components/icons/MenuDotsVertical";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";

export const HOME_STATE = "HOME_STATE";

const shouldShowField = (
  field: EntityDataAttribute,
  allFields: EntityDataAttribute[]
): boolean => {
  if (!field.show) {
    return true;
  }

  const { when, value: showValue } = field.show;
  const dependentField = allFields.find((f) => f.name === when);

  if (!dependentField) {
    return true;
  }

  return dependentField.value === showValue;
};

export const FilePrview = ({ file }: { file: FileObject }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { file_type, name } = file;

  const {
    close: closePreviewModal,
    isOpen: isPreviewModalOpen,
    open: openPreviewModal,
  } = useModal();

  return (
    <>
      <ConditionalToolTip condition={name}>
        <button
          className="all:unset t-flex t-items-start t-gap-1 t-w-10/12"
          onClick={openPreviewModal}
          type="button"
        >
          <FileIcon fileType={file_type} size="24" />
          <div className="t-line-clamp-1 t-ml-1 t-text-body-sm t-text-text-black">
            {name}
          </div>
        </button>
      </ConditionalToolTip>
      <Preview
        showModal={isPreviewModalOpen}
        closeModal={closePreviewModal}
        groupId={groupId}
        previewId={file.uuid}
      />
    </>
  );
};

const String = ({
  field,
  showFieldWithEmptyValue,
}: {
  field: EntityDataAttribute;
  showFieldWithEmptyValue?: boolean;
}) => {
  const { can_copy, value } = field;
  const { copyToClipboard } = useCopyToClipboard();

  if (!value && !showFieldWithEmptyValue) return null;

  return (
    <div className="t-flex t-flex-col">
      <div className="t-text-body-sm t-text-text-60">{field.label}</div>
      <div className="t-flex t-items-center t-text-body-sm t-text-text-black t-gap-1">
        <span className="t-line-clamp-1"> {(value as string) || "-"}</span>
        {can_copy && (
          <button
            onClick={() => copyToClipboard(value as string)}
            className="all:unset"
          >
            <img src={CopyIcon} alt="copyIcon" />
          </button>
        )}
      </div>
    </div>
  );
};

const DateField = ({
  field,
  showFieldWithEmptyValue,
}: {
  field: EntityDataAttribute;
  showFieldWithEmptyValue?: boolean;
}) => {
  const { value } = field;

  if (!value && !showFieldWithEmptyValue) return null;

  return (
    <div className="t-flex t-flex-col">
      <div className="t-text-body-sm t-text-text-60">{field.label}</div>
      <div className="t-flex t-items-center t-text-body-sm t-text-text-black">
        {value ? dayjs(value as string).format(DISPLAY_DATE) : "-"}
      </div>
    </div>
  );
};

const FileField = ({
  field,
  showFieldWithEmptyValue,
}: {
  field: EntityDataAttribute;
  showFieldWithEmptyValue?: boolean;
}) => {
  const { value = [] } = field;
  const files = useMultipleFileDetails(value as string[]);

  if ((value as string[])?.length === 0 && !showFieldWithEmptyValue)
    return null;

  return (
    <div className="t-flex t-flex-col">
      <div className="t-text-body-sm t-text-text-60">{field.label}</div>
      <div className="t-flex t-flex-col t-items-start t-text-body-sm t-text-text-black">
        {files.length > 0
          ? files.map((file) => <FilePrview file={file} key={file.uuid} />)
          : "-"}
      </div>
    </div>
  );
};

const FieldRenderer = ({
  field,
  showFieldWithEmptyValue,
}: {
  field: EntityDataAttribute;
  showFieldWithEmptyValue?: boolean;
}) => {
  const { type } = field;
  switch (type) {
    case "STRING":
      return (
        <String
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    case "NUMBER":
      return (
        <String
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    case "INPUT":
      return (
        <String
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    case "TEXTAREA":
      return (
        <String
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    case "DATE":
      return (
        <DateField
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    case "FILE":
      return (
        <FileField
          field={field}
          showFieldWithEmptyValue={showFieldWithEmptyValue}
        />
      );
    default:
      return null;
  }
};

export const JurisdictionStateCard = ({
  stateData,
  entityId,
  showFieldWithEmptyValue,
  rowGap = "t-gap-y-5",
  stateRegistrationType,
}: {
  stateData: EntityStateData;
  entityId?: string;
  showFieldWithEmptyValue?: boolean;
  rowGap?: string;
  stateRegistrationType?: state_registration_type;
}) => {
  const { isAdmin } = useRoleBasedView();

  const {
    state,
    registration_type,
    foreign_qualified_data_attributes = [],
    state_id,
    uuid,
  } = stateData;

  const isNotHomeState = registration_type !== HOME_STATE;

  const editModal = useModal();
  const deleteModal = useModal();

  return (
    <div className="t-p-5 t-border t-border-solid t-rounded-lg t-border-neutral-10  t-gap-5 t-flex t-flex-col">
      <div className="t-flex t-items-center t-justify-between">
        <span className="t-text-text-100 t-text-subtitle t-flex t-gap-2 t-items-center">
          {state}
          <Tag
            tagType={
              REGISTRATION_TYPE_BADGE[
                registration_type
              ] as TagTypeProps["tagType"]
            }
            icon={false}
          >
            {REGISTRATION_TYPE[registration_type]}
          </Tag>
        </span>
        <div className="t-flex t-items-center t-gap-2">
          <Button
            size="small"
            type="button"
            customType="ghost_icon"
            onClick={editModal.open}
          >
            <span className="t-text-text-30">
              <Pencil color="currentColor" size="16" />
            </span>
          </Button>
          {isNotHomeState && isAdmin && (
            <Button
              size="small"
              type="button"
              customType="ghost_icon"
              onClick={deleteModal.open}
            >
              <span className="t-text-text-30">
                <DeleteIcon color="currentColor" size="16" />
              </span>
            </Button>
          )}
        </div>
      </div>

      <div className={`t-grid t-grid-cols-2 t-gap-x-5 ${rowGap}`}>
        {foreign_qualified_data_attributes?.map((field) => {
          if (
            stateRegistrationType === HOME_STATE &&
            field.name.includes("foreign_qualification_date")
          ) {
            return null;
          }

          if (!shouldShowField(field, foreign_qualified_data_attributes)) {
            return null;
          }

          return (
            <FieldRenderer
              field={field}
              key={field.uuid}
              showFieldWithEmptyValue={showFieldWithEmptyValue}
            />
          );
        })}
      </div>
      <EditJurisdiction
        isOpen={editModal.isOpen}
        close={editModal.close}
        stateId={state_id}
        entityId={entityId}
      />
      <DeleteJurisdictions
        isOpen={deleteModal.isOpen}
        close={deleteModal.close}
        stateId={state_id}
        entityId={entityId}
      />
    </div>
  );
};

const getAttentionLabel = (count: number): string => {
  if (count === 1) return "1 item needs your attention";
  return `${count} items need your attention`;
};

export const JurisdictionStateCardv2 = ({
  stateData,
  entityId,
  showFieldWithEmptyValue,
  rowGap = "t-gap-y-5",
  stateRegistrationType,
  isUSEntity = true,
  attentionCount = 0,
}: {
  stateData: EntityStateData;
  entityId?: string;
  showFieldWithEmptyValue?: boolean;
  rowGap?: string;
  stateRegistrationType?: state_registration_type;
  isUSEntity?: boolean;
  attentionCount?: number;
}) => {
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();

  const {
    state,
    registration_type,
    foreign_qualified_data_attributes = [],
    state_id,
    uuid,
    withdrawal_date,
  } = stateData;

  const isNotHomeState = registration_type !== HOME_STATE;
  const isWithdrawn = Boolean(withdrawal_date);
  const withdrawActionTooltip = isWithdrawn ? "Reinstate" : "Withdraw";

  const editModal = useModal();
  const deleteModal = useModal();
  const withdrawModal = useModal();

  // Deferring the modal open avoids a Radix bug where a DropdownMenu.Item
  // opening a Dialog in the same tick can leave `pointer-events: none`
  // stuck on <body> after the dropdown's dismiss-layer cleanup races the
  // dialog's own open, making the whole page unclickable. The dropdown
  // must still auto-close as normal, so onSelect is left un-prevented.
  const openAfterDropdownCloses = (open: () => void) => () => {
    setTimeout(open, 0);
  };
  const handleEditSelect = openAfterDropdownCloses(editModal.open);
  const handleWithdrawSelect = openAfterDropdownCloses(withdrawModal.open);
  const handleDeleteSelect = openAfterDropdownCloses(deleteModal.open);

  return (
    <div className="t-p-5 t-border t-border-solid t-rounded-lg t-border-neutral-10  t-gap-5 t-flex t-flex-col">
      <div className="t-flex t-items-start t-justify-between t-gap-2 t-border t-border-solid t-border-neutral-10 t-border-t-0 t-border-l-0 t-border-r-0 t-pb-4">
        <span className="t-text-text-100 t-text-subtitle t-flex t-flex-col t-gap-1.5 t-items-start">
          <span className="t-flex t-gap-2 t-items-center">
            {state}
            {registration_type === HOME_STATE && (
              <Tag
                tagType={
                  REGISTRATION_TYPE_BADGE[
                    registration_type
                  ] as TagTypeProps["tagType"]
                }
                icon={false}
                rounded
              >
                {REGISTRATION_TYPE[registration_type]}
              </Tag>
            )}
          </span>
          {isWithdrawn && (
            <Tag tagType="red" rounded size="small" transparent>
              <span className="t-flex t-items-center t-gap-1">
                Withdrawn
                <ToolTip
                  text={`Withdrawn on ${dayjs(withdrawal_date).format(
                    DISPLAY_DATE
                  )}`}
                >
                  <span className="t-flex t-items-center">
                    <Info size="14" />
                  </span>
                </ToolTip>
              </span>
            </Tag>
          )}
        </span>
        <div className="t-flex t-items-center t-gap-1 t-shrink-0">
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <div>
                <Button
                  type="button"
                  customType="ghost_icon"
                  size="small"
                  aria-label="Jurisdiction options"
                >
                  <span className="t-flex t-text-text-30">
                    <MenuDotsVertical />
                  </span>
                </Button>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end" sideOffset={4}>
                <Dropdown.Item textValue="Edit" onSelect={handleEditSelect}>
                  Edit
                </Dropdown.Item>
                {isAdmin && (
                  <Dropdown.Item
                    textValue={withdrawActionTooltip}
                    onSelect={handleWithdrawSelect}
                    disabled={!isNotHomeState}
                  >
                    {withdrawActionTooltip}
                  </Dropdown.Item>
                )}
                {isAdmin && (
                  <Dropdown.Item
                    textValue="Delete"
                    onSelect={handleDeleteSelect}
                    disabled={!isNotHomeState}
                    type="danger"
                  >
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>

      <div className="t-flex t-flex-col t-gap-6">
        {foreign_qualified_data_attributes?.map((field) => {
          if (
            stateRegistrationType === HOME_STATE &&
            field.name.includes("foreign_qualification_date")
          ) {
            return null;
          }

          if (!shouldShowField(field, foreign_qualified_data_attributes)) {
            return null;
          }

          return (
            <FieldRenderer
              field={field}
              key={field.uuid}
              showFieldWithEmptyValue={showFieldWithEmptyValue}
            />
          );
        })}
      </div>

      {isUSEntity && entityId && !isWithdrawn && attentionCount > 0 && (
        <Link
          to={
            isAdmin
              ? `/admin/crm/${groupId}/state-compliance/${state_id}?entity=${entityId}`
              : `${TAX_ROUTE}/state-compliance/${state_id}?entity=${entityId}`
          }
          className="t-flex t-items-center t-gap-2 t-rounded-lg t-border t-border-solid t-border-yellow-20 t-bg-yellow-10 t-px-3 t-py-2 t-no-underline"
        >
          <Warning size="16" />
          <span className="t-text-body-sm t-text-text-100 t-flex-1">
            {getAttentionLabel(attentionCount)}
          </span>
          <span className="t-text-text-30 t-shrink-0">
            <ArrowRight color="currentColor" size="16" />
          </span>
        </Link>
      )}
      <EditJurisdiction
        isOpen={editModal.isOpen}
        close={editModal.close}
        stateId={state_id}
        entityId={entityId}
      />
      <DeleteJurisdictions
        isOpen={deleteModal.isOpen}
        close={deleteModal.close}
        stateId={state_id}
        entityId={entityId}
      />
      <WithdrawJurisdiction
        isOpen={withdrawModal.isOpen}
        close={withdrawModal.close}
        stateId={state_id}
        entityId={entityId}
        isWithdrawn={isWithdrawn}
      />
    </div>
  );
};

const Jurisdictions = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { uuid: groupId, entities } = useCurrentGroupContext();

  const entity = entities.find((entity) => entity.uuid === entityId);
  const { country_code } = entity || {};
  const isUSEntity = country_code === "US";

  const {
    data = [],
    isLoading,
    isSuccess,
  } = useGetEntityAllStateDataQuery(
    { entityId, groupId },
    { skip: !groupId || !entityId }
  );

  const { data: stateRegistrations = [] } = useGetStateRegistrationsQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !groupId || !entityId || !isUSEntity }
  );

  const { data: entityAttributes = [] } = useGetEntityDataAttributesQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !groupId || !entityId || !isUSEntity }
  );

  const { close, isOpen, open } = useModal();

  const sortedData = [...(data ?? [])].sort((a, b) => {
    if (a?.registration_type === HOME_STATE) return -1;
    if (b?.registration_type === HOME_STATE) return 1;
    return 0;
  });

  const isEmpty = data?.length === 0;

  if (isLoading) {
    return null;
  }

  return (
    <Async.Root isEmpty={isEmpty} isLoading={isLoading} isSuccess={isSuccess}>
      <Async.Empty>
        <div className="t-flex t-gap-3 t-flex-col">
          <div className="t-flex t-items-center t-justify-between t-text-subtitle t-text-text-100">
            Jurisdictions
            {isUSEntity && (
              <Button size="small" onClick={open}>
                Add
              </Button>
            )}
          </div>
        </div>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-gap-3 t-flex-col">
          <div className="t-flex t-items-center t-justify-between t-text-subtitle t-text-text-100">
            <span>
              Jurisdictions{" "}
              <ToolTip
                text="States where your business operates. Including sales activity, employees, or a physical presence."
                propClass="!t-w-72"
              >
                <span className="t-text-text-30 -t-pt-1">
                  <Info />
                </span>
              </ToolTip>
            </span>
            {isUSEntity && (
              <Button size="small" onClick={open}>
                Add
              </Button>
            )}
          </div>
          {sortedData?.map((stateData) => {
            const stateReg = stateRegistrations.find(
              (reg) => reg.state_id === stateData.state_id
            );
            const attentionCount = stateReg
              ? getStateAttentionCount(stateReg, entityAttributes)
              : 0;

            return (
              <JurisdictionStateCardv2
                stateData={stateData}
                key={stateData.uuid}
                entityId={entityId}
                showFieldWithEmptyValue
                isUSEntity={isUSEntity}
                attentionCount={attentionCount}
              />
            );
          })}
        </div>
      </Async.Success>
      {isOpen && <AddJurisdiction isOpen={isOpen} close={close} />}
    </Async.Root>
  );
};

export default Jurisdictions;
